import {inject, Injectable} from '@angular/core';
import {forkJoin, map, Observable, of, switchMap} from 'rxjs';
import {Exception} from '../../models/error/Exception';
import {BaseErrorService} from './base-error.service';
import {TenantService} from '../tenant/tenant.service';
import {WatcherHttpClient} from '../httpClient/WatcherHttpClient';
import {ErrorFilter} from '../../models/filter/ErrorFilter';
import {ErrorLog} from '../../models/error/ErrorLog';
import {ExceptionAPI} from '../../api/models/error/ExceptionApi';
import { ErrorPriority } from '../../enums/ErrorPriority';

@Injectable({
    providedIn: 'root'
})
export class ExceptionService extends BaseErrorService<Exception> {
    protected readonly necessaryExceptionFields: string[] = [
        "Message",
        "StackTrace"
    ];

    private watcherHttpClient: WatcherHttpClient = inject(WatcherHttpClient);
    private tenantService: TenantService = inject(TenantService);

    public getAmountOfErrorItems(errorFilter: ErrorFilter): Observable<number> {
        return this.watcherHttpClient.get<number>("/exception/$count" + this.createODataQueryForAllErrorItems(errorFilter))
    }

    public updateErrorType(exception: Exception, newPriority: ErrorPriority): Observable<void> {
        return this.watcherHttpClient.patch<void>("/exception/" + exception.id, {
            "errorPriority": newPriority
        });
    }

    public solveError(exception: Exception): Observable<void> {
        return this.watcherHttpClient.patch<void>("/exception/" + exception.id, {
            "isSolved": true
        });
    }

    protected getAllWithFilter(filter: string): Observable<Exception[]> {
        return this.watcherHttpClient.get<ExceptionAPI[]>("/exception" + filter).pipe(
            switchMap((exceptions: ExceptionAPI[]) => exceptions?.length > 0 ?
                forkJoin(exceptions.map(exception => this.createException(exception))) : of([])
            )
        );
    }

    public getMostRecentErrorItems(maxAmountOfItems: number): Observable<Exception[]> {
        return this.watcherHttpClient.get<ExceptionAPI[]>("/exception/summary?take=" + maxAmountOfItems).pipe(
            switchMap((exceptions: ExceptionAPI[]) => exceptions?.length > 0 ?
                forkJoin(exceptions.map(exception => this.createException(exception))) : of([])
            )
        );
    }

    private createException(exceptionAPI: ExceptionAPI): Observable<Exception> {
        return forkJoin({
            tenant: this.tenantService.getTenant(exceptionAPI.tenantId),
            application: this.tenantService.getApplication(exceptionAPI.applicationId, exceptionAPI.tenantId)
        }).pipe(
            map(({tenant, application}) => {
                return new Exception(
                    exceptionAPI.id,
                    tenant,
                    application,
                    exceptionAPI.mostRecentOccurred,
                    exceptionAPI.occurrenceCount,
                    exceptionAPI.errorPriority,
                    exceptionAPI.isSolved,
                    exceptionAPI.message,
                    exceptionAPI.stackTrace,
                    exceptionAPI.errorLogs?.map(errorLogApi => new ErrorLog(
                        errorLogApi.id,
                        errorLogApi.occurred
                    )),
                    exceptionAPI.solution
                )
            })
        );
    }

    protected override getNecessaryFieldsForErrors(): string[] {
        const necessaryFields: string[] = super.getNecessaryFieldsForErrors();
        necessaryFields.push(...this.necessaryExceptionFields);
        return necessaryFields;
    }
}
